import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { processService } from "../../../services/process.service";

const TIPOS_HERDEIRO = ["cônjuge", "filho(a)", "pai/mãe", "irmão/irmã", "outro"];
const TIPO_CONTA_OPTIONS = ["corrente", "poupanca"];
const TIPO_PIX_OPTIONS = ["cpf", "email", "telefone", "aleatorio"];

const EMPTY_HERDEIRO = {
    nome: "",
    cpf: "",
    tipo_herdeiro: "",
    percentual: "",
    banco: "",
    agencia: "",
    conta_corrente: "",
    digito_conta: "",
    tipo_conta: "",
    pix: "",
    tipo_pix: "",
    endereco_logradouro: "",
    endereco_numero: "",
    endereco_complemento: "",
    endereco_bairro: "",
    endereco_cidade: "",
    endereco_estado: "",
    endereco_cep: "",
};

function Field({ label, children, optional = false }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {label}
                {optional && <span className="text-muted-foreground font-normal normal-case ml-1 text-xs">(opcional)</span>}
            </Label>
            {children}
        </div>
    );
}

function HerdeiroRow({ index, herdeiro, onChange, onRemove, totalOutros }) {
    const set = (key) => (e) => onChange(index, key, e.target.value);
    const setSelect = (key) => (e) => onChange(index, key, e.target.value);

    const pct = parseFloat(herdeiro.percentual) || 0;
    const otherSum = totalOutros;
    const wouldExceed = otherSum + pct > 100;

    return (
        <div className="border rounded-lg p-4 space-y-3 bg-muted relative">
            <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-foreground">Herdeiro {index + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-red-500 hover:text-statusError h-7 w-7 p-0">
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Nome">
                    <Input value={herdeiro.nome} onChange={set("nome")} placeholder="Nome completo" className="h-8 text-sm" />
                </Field>
                <Field label="CPF">
                    <Input value={herdeiro.cpf} onChange={set("cpf")} placeholder="000.000.000-00" className="h-8 text-sm" />
                </Field>
                <Field label="Tipo de Herdeiro">
                    <select
                        value={herdeiro.tipo_herdeiro}
                        onChange={setSelect("tipo_herdeiro")}
                        className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                        <option value="">Selecione...</option>
                        {TIPOS_HERDEIRO.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                <Field label="Percentual (%)">
                    <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={herdeiro.percentual}
                        onChange={set("percentual")}
                        placeholder="Ex: 50"
                        className={`h-8 text-sm ${wouldExceed ? "border-red-400" : ""}`}
                    />
                    {wouldExceed && (
                        <p className="text-xs text-red-500">Soma ultrapassa 100%</p>
                    )}
                </Field>
            </div>

            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-2">Dados Bancários</p>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Banco" optional>
                    <Input value={herdeiro.banco} onChange={set("banco")} placeholder="Ex: Bradesco" className="h-8 text-sm" />
                </Field>
                <Field label="Agência" optional>
                    <Input value={herdeiro.agencia} onChange={set("agencia")} placeholder="0000" className="h-8 text-sm" />
                </Field>
                <Field label="Conta" optional>
                    <Input value={herdeiro.conta_corrente} onChange={set("conta_corrente")} placeholder="00000" className="h-8 text-sm" />
                </Field>
                <Field label="Dígito" optional>
                    <Input value={herdeiro.digito_conta} onChange={set("digito_conta")} placeholder="0" className="h-8 text-sm" />
                </Field>
                <Field label="Tipo de Conta" optional>
                    <select
                        value={herdeiro.tipo_conta}
                        onChange={setSelect("tipo_conta")}
                        className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                        <option value="">Selecione...</option>
                        {TIPO_CONTA_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                <Field label="PIX" optional>
                    <Input value={herdeiro.pix} onChange={set("pix")} placeholder="Chave PIX" className="h-8 text-sm" />
                </Field>
                <Field label="Tipo de PIX" optional>
                    <select
                        value={herdeiro.tipo_pix}
                        onChange={setSelect("tipo_pix")}
                        className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                        <option value="">Selecione...</option>
                        {TIPO_PIX_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
            </div>

            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-2">Endereço</p>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Logradouro" optional>
                    <Input value={herdeiro.endereco_logradouro} onChange={set("endereco_logradouro")} placeholder="Rua, Av..." className="h-8 text-sm" />
                </Field>
                <Field label="Número" optional>
                    <Input value={herdeiro.endereco_numero} onChange={set("endereco_numero")} placeholder="123" className="h-8 text-sm" />
                </Field>
                <Field label="Complemento" optional>
                    <Input value={herdeiro.endereco_complemento} onChange={set("endereco_complemento")} placeholder="Apto, Bloco..." className="h-8 text-sm" />
                </Field>
                <Field label="Bairro" optional>
                    <Input value={herdeiro.endereco_bairro} onChange={set("endereco_bairro")} placeholder="Bairro" className="h-8 text-sm" />
                </Field>
                <Field label="Cidade" optional>
                    <Input value={herdeiro.endereco_cidade} onChange={set("endereco_cidade")} placeholder="Cidade" className="h-8 text-sm" />
                </Field>
                <Field label="Estado" optional>
                    <Input value={herdeiro.endereco_estado} onChange={set("endereco_estado")} placeholder="SP" maxLength={2} className="h-8 text-sm" />
                </Field>
                <Field label="CEP" optional>
                    <Input value={herdeiro.endereco_cep} onChange={set("endereco_cep")} placeholder="00000-000" className="h-8 text-sm" />
                </Field>
            </div>
        </div>
    );
}

export function HerdeiroDialog({ isOpen, onOpenChange, cedente, onConfirm }) {
    const [herdeiros, setHerdeiros] = useState([{ ...EMPTY_HERDEIRO }]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setHerdeiros([{ ...EMPTY_HERDEIRO }]);
        }
    }, [isOpen, cedente]);

    const totalPct = herdeiros.reduce((sum, h) => sum + (parseFloat(h.percentual) || 0), 0);
    const canSubmit = totalPct <= 100 && (herdeiros.length === 0 || totalPct === 100);

    const handleChange = (index, key, value) => {
        setHerdeiros(prev => prev.map((h, i) => i === index ? { ...h, [key]: value } : h));
    };

    const handleAdd = () => setHerdeiros(prev => [...prev, { ...EMPTY_HERDEIRO }]);

    const handleRemove = (index) => setHerdeiros(prev => prev.filter((_, i) => i !== index));

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        try {
            for (const h of herdeiros) {
                if (!h.nome && !h.cpf) continue;
                const payload = {};
                Object.entries(h).forEach(([k, v]) => {
                    if (v !== "" && v !== null && v !== undefined) payload[k] = v;
                });
                if (payload.percentual !== undefined) payload.percentual = parseFloat(payload.percentual);
                await processService.createHerdeiro(cedente.id, payload);
            }
            toast.success("Herdeiros salvos com sucesso");
            onConfirm();
        } catch (err) {
            toast.error("Erro ao salvar herdeiros: " + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    const pctColor = totalPct > 100 ? "text-red-600 bg-statusErrorBg border-statusError/20" :
                     totalPct === 100 ? "text-green-700 bg-green-50 border-green-200" :
                     "text-amber-600 bg-statusWarningBg border-statusWarning/20";

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        Registrar Herdeiros — {cedente?.nome}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                        Preencha os dados dos herdeiros antes de confirmar o falecimento. A soma dos percentuais deve ser 100%.
                    </p>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {herdeiros.map((h, i) => {
                        const othersSum = herdeiros.reduce((s, x, idx) => s + (idx !== i ? (parseFloat(x.percentual) || 0) : 0), 0);
                        return (
                            <HerdeiroRow
                                key={i}
                                index={i}
                                herdeiro={h}
                                onChange={handleChange}
                                onRemove={handleRemove}
                                totalOutros={othersSum}
                            />
                        );
                    })}

                    <Button variant="outline" size="sm" onClick={handleAdd} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar herdeiro
                    </Button>

                    {herdeiros.length > 0 && (
                        <div className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm font-medium ${pctColor}`}>
                            <span>Total dos percentuais</span>
                            <span>{totalPct.toFixed(2).replace(".", ",")}%</span>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={saving || !canSubmit}
                        className="bg-gray-800 hover:bg-gray-900 text-white"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Salvar e Confirmar Falecimento
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
